import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, FileImage, FileText, Loader2, AlertCircle, Scan, Activity, Shield } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import { toast } from "sonner";
import axios from "axios";
import { LabsLayout } from "../../components/LabsLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MedPlexityLabs() {
  const navigate = useNavigate();
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  }, []);

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;

    const validTypes = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
    if (!validTypes.some(type => selectedFile.type.includes(type.split("/")[1]))) {
      toast.error("Invalid file type. Please upload JPEG, PNG, WEBP images or PDF files.");
      return;
    }

    setFile(selectedFile);

    if (selectedFile.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(selectedFile);
    } else {
      setPreview(null);
    }
  };

  const handleAnalyze = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }

    setIsAnalyzing(true);
    setProgress(0);

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 15;
      });
    }, 500);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/labs/analyze`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true
      });

      setProgress(100);
      clearInterval(progressInterval);

      toast.success("Analysis complete!");
      
      setTimeout(() => {
        navigate(`/labs/analysis/${response.data.id}`);
      }, 500);

    } catch (error) {
      clearInterval(progressInterval);
      console.error("Analysis error:", error);
      toast.error(error.response?.data?.detail || "Analysis failed. Please try again.");
    } finally {
      setIsAnalyzing(false);
      setProgress(0);
    }
  };

  const clearFile = () => {
    setFile(null);
    setPreview(null);
  };

  return (
    <LabsLayout>
      <div className="min-h-[calc(100vh-4rem)]">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          {/* Background */}
          <div className="absolute inset-0 bg-gradient-to-b from-blue-950/20 to-transparent" />
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                AI-Powered Medical
                <span className="text-purple-400"> Image Analysis</span>
              </h1>
              <p className="text-lg md:text-xl text-zinc-400 max-w-2xl mx-auto leading-relaxed">
                Upload your MRI, X-ray, CT scan, or radiology report for instant AI interpretation 
                with detailed findings and clinical recommendations.
              </p>
            </div>

            {/* Upload Section */}
            <div className="max-w-3xl mx-auto">
              <Card className="bg-zinc-900/50 border-zinc-800 overflow-hidden">
                <CardContent className="p-8">
                  {!file ? (
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      data-testid="upload-dropzone"
                      className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all ${
                        isDragging ? "border-blue-500 bg-blue-500/5" : "border-zinc-700 hover:border-zinc-600"
                      }`}
                      onClick={() => document.getElementById("file-input").click()}
                    >
                      <input
                        id="file-input"
                        type="file"
                        className="hidden"
                        accept="image/jpeg,image/png,image/webp,application/pdf"
                        onChange={(e) => handleFileSelect(e.target.files[0])}
                        data-testid="file-input"
                      />
                      
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-20 h-20 rounded-2xl bg-zinc-800 flex items-center justify-center">
                          <Upload className="w-10 h-10 text-blue-500" />
                        </div>
                        <div>
                          <p className="text-lg font-medium text-white mb-2">
                            Drop your medical image here
                          </p>
                          <p className="text-sm text-zinc-500">
                            or click to browse • JPEG, PNG, WEBP, PDF
                          </p>
                        </div>
                        <div className="flex items-center gap-6 mt-4">
                          <div className="flex items-center gap-2 text-zinc-500">
                            <FileImage className="w-5 h-5" />
                            <span className="text-sm">X-Ray</span>
                          </div>
                          <div className="flex items-center gap-2 text-zinc-500">
                            <Scan className="w-5 h-5" />
                            <span className="text-sm">CT/MRI</span>
                          </div>
                          <div className="flex items-center gap-2 text-zinc-500">
                            <FileText className="w-5 h-5" />
                            <span className="text-sm">Reports</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* File Preview */}
                      <div className="relative rounded-xl overflow-hidden bg-zinc-950 border border-zinc-800">
                        {preview ? (
                          <img
                            src={preview}
                            alt="Preview"
                            className="w-full max-h-[400px] object-contain"
                            data-testid="image-preview"
                          />
                        ) : (
                          <div className="flex items-center justify-center py-20">
                            <div className="flex flex-col items-center gap-3 text-zinc-500">
                              <FileText className="w-16 h-16" />
                              <span className="font-medium">{file.name}</span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* File Info */}
                      <div className="flex items-center justify-between p-4 bg-zinc-950 rounded-lg border border-zinc-800">
                        <div className="flex items-center gap-3">
                          {file.type.startsWith("image/") ? (
                            <FileImage className="w-5 h-5 text-blue-500" />
                          ) : (
                            <FileText className="w-5 h-5 text-blue-500" />
                          )}
                          <div>
                            <p className="text-sm font-medium text-white truncate max-w-xs">
                              {file.name}
                            </p>
                            <p className="text-xs text-zinc-500">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearFile}
                          disabled={isAnalyzing}
                          data-testid="clear-file-btn"
                          className="text-zinc-400 hover:text-white"
                        >
                          Remove
                        </Button>
                      </div>

                      {/* Progress */}
                      {isAnalyzing && (
                        <div className="space-y-3" data-testid="analysis-progress">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-zinc-400">Analyzing image...</span>
                            <span className="text-blue-500 font-medium">{Math.round(progress)}%</span>
                          </div>
                          <Progress value={progress} className="h-2 bg-zinc-800" />
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex gap-4">
                        <Button
                          onClick={handleAnalyze}
                          disabled={isAnalyzing}
                          data-testid="analyze-btn"
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-6"
                        >
                          {isAnalyzing ? (
                            <>
                              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                              Analyzing...
                            </>
                          ) : (
                            <>
                              <Scan className="w-5 h-5 mr-2" />
                              Analyze Image
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Features */}
            <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-6">
              <FeatureCard
                icon={Scan}
                title="Multi-Modal Analysis"
                description="Supports X-rays, CT scans, MRIs, HRCT, and radiology reports"
              />
              <FeatureCard
                icon={Activity}
                title="Detailed Findings"
                description="Get comprehensive interpretations with differential diagnoses"
              />
              <FeatureCard
                icon={Shield}
                title="Clinical Recommendations"
                description="Receive suggested follow-up tests and red-flag warnings"
              />
            </div>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="py-8 border-t border-zinc-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-start gap-3 p-4 bg-zinc-900/50 rounded-lg border border-zinc-800">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-zinc-400">
                <span className="font-medium text-zinc-300">Medical Disclaimer:</span> This AI tool is 
                intended for educational and informational purposes only. It should not replace 
                professional medical advice, diagnosis, or treatment. Always consult qualified 
                healthcare providers for medical decisions.
              </p>
            </div>
          </div>
        </section>
      </div>
    </LabsLayout>
  );
}

const FeatureCard = ({ icon: Icon, title, description }) => (
  <Card className="bg-zinc-900/30 border-zinc-800 hover:border-zinc-700 transition-colors">
    <CardContent className="p-6">
      <div className="w-12 h-12 rounded-lg bg-blue-600/10 flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-blue-500" />
      </div>
      <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h3>
      <p className="text-zinc-400 text-sm leading-relaxed">{description}</p>
    </CardContent>
  </Card>
);
